import { useSelector, shallowEqual } from 'react-redux';

import { selectors as meterTypeSelectors } from 'state/meter-type/meter-type-slice';
import { selectors as meterpointValidationSelectors } from 'state/meterpoint-validation/meterpoint-validation-slice';
import { METER_TYPE_ERROR_CODES } from 'config/app-config';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';

const includesErrorCodes = (errors, errorCodeList) =>
  errors.some(({ code }) => errorCodeList.includes(code));

export const useMeterSelectors = () => {
  const {
    selectValidationRequired,
    selectUserMeterpoints
  } = meterpointValidationSelectors;
  const meterpointValidationRequired = useSelector(
    selectValidationRequired,
    shallowEqual
  );
  const { MPAN, MPRN } = useSelector(selectUserMeterpoints, shallowEqual);
  const isMeterpointValidationRequired = Object.values(
    meterpointValidationRequired
  ).some((v) => v);

  const electricityMeterSelectors = MPAN
    ? meterpointValidationSelectors
    : meterTypeSelectors;
  const gasMeterSelectors = MPRN
    ? meterpointValidationSelectors
    : meterTypeSelectors;
  const commonSelectors =
    MPAN || MPRN ? meterpointValidationSelectors : meterTypeSelectors;

  const {
    selectElectricityMeter,
    selectHasE7Meter,
    selectHasElectricityTOUMeter,
    selectHasElectricityPrepayMeter,
    selectElectricityErrors,
    selectIsRelatedElectricityMeter
  } = electricityMeterSelectors;

  const {
    selectGasMeter,
    selectGasErrors,
    selectHasGasPrepayMeter,
    selectHasGasTOUMeter
  } = gasMeterSelectors;

  const {
    selectErrors,
    selectIsBGECustomer,
    selectIsBGCustomer
  } = commonSelectors;

  return {
    selectElectricityMeter,
    selectHasE7Meter,
    selectHasElectricityTOUMeter,
    selectHasElectricityPrepayMeter,
    selectElectricityErrors,
    selectIsRelatedElectricityMeter,
    selectGasMeter,
    selectHasGasPrepayMeter,
    selectHasGasTOUMeter,
    selectGasErrors,
    isMeterpointValidationRequired,
    selectErrors,
    selectIsBGECustomer,
    selectIsBGCustomer
  };
};

export const useMeterType = () => {
  const {
    selectElectricityMeter,
    selectHasE7Meter,
    selectHasElectricityTOUMeter,
    selectHasElectricityPrepayMeter,
    selectElectricityErrors,
    selectIsRelatedElectricityMeter,
    selectGasMeter,
    selectHasGasPrepayMeter,
    selectHasGasTOUMeter,
    selectGasErrors,
    isMeterpointValidationRequired,
    selectIsBGECustomer,
    selectIsBGCustomer
  } = useMeterSelectors();
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const UNUSPPORTED_METER_ERRORS = [
    METER_TYPE_ERROR_CODES.MeterMeasurementClassException,
    METER_TYPE_ERROR_CODES.MHHSUnmeteredSuppliesException,
    METER_TYPE_ERROR_CODES.MeterProfileClassException,
    METER_TYPE_ERROR_CODES.MHHSMeterProfileClassException,
    METER_TYPE_ERROR_CODES.MeterDeenergisedException,
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierBGREException,
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierNEPException,
    METER_TYPE_ERROR_CODES.MeterRelatedException
  ];

  const UNELIGIBLE_METER_ERRORS = [METER_TYPE_ERROR_CODES.MeterEVException];

  const electricityMeter = useSelector(selectElectricityMeter, shallowEqual);
  const isRelatedElectricityMeter = useSelector(
    selectIsRelatedElectricityMeter,
    shallowEqual
  );
  const gasMeter = useSelector(selectGasMeter, shallowEqual);
  const isElecPrepayMeter = useSelector(
    selectHasElectricityPrepayMeter,
    shallowEqual
  );
  const isGasPrepayMeter = useSelector(selectHasGasPrepayMeter, shallowEqual);

  const hasElectricityPrepayMeter = fuelType.electric && isElecPrepayMeter;
  const hasGasPrepayMeter = fuelType.gas && isGasPrepayMeter;

  const hasAnyPrepayMeter = hasElectricityPrepayMeter || hasGasPrepayMeter;

  const hasElectricityMeter = !!electricityMeter;
  const electricityErrors = useSelector(selectElectricityErrors, shallowEqual);
  const isUnsupportedElectricityMeter = includesErrorCodes(
    electricityErrors,
    UNUSPPORTED_METER_ERRORS
  );
  const isUneligibleElectricityMeter = includesErrorCodes(
    electricityErrors,
    UNELIGIBLE_METER_ERRORS
  );
  const isEvException = includesErrorCodes(electricityErrors, [
    METER_TYPE_ERROR_CODES.MeterEVException
  ]);

  const hasElecE7Meter = useSelector(selectHasE7Meter, shallowEqual);
  const hasE7Meter = !!(fuelType.electric && hasElecE7Meter); //For gas fuel cannot have two rate meter
  const isElecTOUMeter = useSelector(
    selectHasElectricityTOUMeter,
    shallowEqual
  );
  const isGasTOUMeter = useSelector(selectHasGasTOUMeter, shallowEqual);
  const hasElectricityTOUMeter = fuelType.electric && isElecTOUMeter;
  const hasGasTOUMeter = fuelType.gas && isGasTOUMeter;
  const hasAnyTOUMeter = hasElectricityTOUMeter || hasGasTOUMeter;

  const hasGasMeter = !!gasMeter;
  const gasErrors = useSelector(selectGasErrors, shallowEqual);
  const isUnsupportedGasMeter = includesErrorCodes(
    gasErrors,
    UNUSPPORTED_METER_ERRORS
  );
  const isUneligibleGasMeter = includesErrorCodes(
    gasErrors,
    UNELIGIBLE_METER_ERRORS
  );

  const isBGECustomer = useSelector(selectIsBGECustomer, shallowEqual);
  const isBGCustomer = useSelector(selectIsBGCustomer, shallowEqual);
  const isBGCustomerForGas = includesErrorCodes(gasErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierBGREException
  ]);
  const isBGCustomerForElec = includesErrorCodes(electricityErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierBGREException
  ]);
  const isBGECustomerForGas = includesErrorCodes(gasErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierNEPException
  ]);
  const isBGECustomerForElec = includesErrorCodes(electricityErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierNEPException
  ]);

  const allMeterErrors = [...electricityErrors, ...gasErrors];
  const meterTypeErrors = allMeterErrors.filter(({ code }) =>
    UNUSPPORTED_METER_ERRORS.includes(code)
  );

  const selectedFuelErrors = [
    ...(fuelType.gas ? gasErrors : []),
    ...(fuelType.electric ? electricityErrors : [])
  ].filter(({ code }) => UNUSPPORTED_METER_ERRORS.includes(code));

  const regionCode = electricityMeter?.regionCode;

  return {
    electricityMeter,
    gasMeter,
    hasElectricityMeter,
    hasGasMeter,
    hasE7Meter,
    hasElectricityTOUMeter,
    hasAnyPrepayMeter,
    hasAnyTOUMeter,
    hasElectricityPrepayMeter,
    hasGasPrepayMeter,
    isGasPrepayMeter,
    isElecPrepayMeter,
    hasGasTOUMeter,
    isUnsupportedElectricityMeter,
    isEvException,
    isUneligibleElectricityMeter,
    isUnsupportedGasMeter,
    isUneligibleGasMeter,
    meterTypeErrors,
    allMeterErrors,
    electricityErrors,
    gasErrors,
    isBGECustomer,
    isMeterpointValidationRequired,
    isBGCustomer,
    selectedFuelErrors,
    isBGCustomerForElec,
    isBGECustomerForElec,
    isBGECustomerForGas,
    isBGCustomerForGas,
    isRelatedElectricityMeter,
    regionCode
  };
};
